import React from 'react';
import { hexToRgba } from '@stackline/ui';
import _isNil from 'lodash/isNil';
import { IconDecrease, IconIncrease, IconNoChange } from 'src/components/BeaconV2Components/icons';
import { HoverTooltipProps } from 'src/components/BeaconV2Components/HoverTooltip/HoverTooltip';

export interface PromotionHoverTooltipProps extends HoverTooltipProps {
  promotionHoverTooltipOptions: {
    promotionTitle: string;
    promotionFormattedChange?: string;
    promotionRawChange?: number;
    promotionPrimaryMetric: string;
    promotionTooltipWrapperStyle?: React.CSSProperties;
    renderAdditionalPromotionData?: () => React.ReactNode;
  };
}

/**
 * Unique promotion tooltip to be displayed when hovering
 * over Beacon Retail Sales graphs.
 *
 * NOTE: This must use pure HTML elements because it will
 * be converted into a string. This is because the Highcharts
 * tooltip formatter expects a string, not JSX.
 */
const PromotionHoverTooltip = ({
  title,
  change: formattedChange,
  rawChange,
  primaryMetric,
  primaryMetricLabel,
  primaryColor,
  secondaryMetric,
  secondaryColor,
  secondaryMetricLabel,
  theme,
  promotionHoverTooltipOptions
}: PromotionHoverTooltipProps) => {
  const fontStyles = {
    fontFamily: 'Roboto',
    fontWeight: 500
  };

  const {
    promotionTitle,
    promotionFormattedChange,
    promotionRawChange,
    promotionPrimaryMetric,
    promotionTooltipWrapperStyle,
    renderAdditionalPromotionData
  } = promotionHoverTooltipOptions;

  const createMetricRow = (color: string, metric: string, metricLabel: string) => {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          color
        }}
      >
        <div
          style={{
            ...fontStyles,
            fontSize: '14px',
            lineHeight: '20px',
            marginRight: theme.spacing.xs
          }}
        >
          {metric}
        </div>
        {metricLabel && (
          <div
            style={{
              ...fontStyles,
              fontSize: '12px'
            }}
          >
            {metricLabel}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      style={{
        border: `1px solid ${theme.colors.primaryGray}`,
        borderRadius: theme.spacing.xs,
        padding: '12px',
        backgroundColor: 'white',
        display: 'inline-block',
        minWidth: '137px',
        width: 'auto',
        height: 'auto',
        ...promotionTooltipWrapperStyle
      }}
    >
      {/* Standard metrics */}

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
        {/* Title, e.g. Week 12 */}
        <span
          style={{
            fontSize: '16px',
            textTransform: 'uppercase',
            color: theme.colors.secondary,
            letterSpacing: '0.96px',
            lineHeight: '28px',
            ...fontStyles
          }}
        >
          {title}
        </span>
        {/* Percentage change next to title, like 9.02% */}
        {!_isNil(rawChange) && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginLeft: theme.spacing.sm,
              color: theme.colors[+rawChange.toFixed(3) === 0 ? 'secondary' : rawChange > 0 ? 'success' : 'error']
            }}
          >
            <div style={{ marginTop: '1px' }}>
              {/* This logic has to be inline for it to function with the static rendering */}
              {+rawChange.toFixed(3) === 0 ? (
                <div
                  style={{
                    transform: 'rotate(0deg)', // pointing to the right
                    color: theme.colors.primary
                  }}
                >
                  {IconNoChange}
                </div>
              ) : rawChange > 0 ? (
                <div style={{ color: theme.colors.success }}>{IconIncrease}</div> // pointing up
              ) : (
                <div
                  style={{
                    transform: 'rotate(180deg)', // pointing down
                    color: theme.colors.error
                  }}
                >
                  {IconDecrease}
                </div>
              )}
            </div>

            <div
              style={{
                marginLeft: theme.spacing.xs,
                fontSize: 12,
                ...fontStyles
              }}
            >
              {formattedChange}
            </div>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {createMetricRow(primaryColor, primaryMetric, primaryMetricLabel)}
        {secondaryMetric && createMetricRow(secondaryColor, secondaryMetric, secondaryMetricLabel)}
      </div>

      {/* Promotion data */}
      <div style={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column', marginTop: '12px' }}>
        <div
          style={{
            display: 'inline-flex',
            padding: '3px 8px',
            borderRadius: '4px',
            backgroundColor: hexToRgba(theme.colors.accentTangerine, 0.1)
          }}
        >
          <span style={{ color: theme.colors.accentTangerine, fontSize: 12, lineHeight: 1.5, ...fontStyles }}>
            {promotionTitle}
          </span>
        </div>
      </div>

      {renderAdditionalPromotionData ? (
        renderAdditionalPromotionData()
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
          <span
            style={{
              color: theme.colors.primary,
              fontSize: '14px',
              fontWeight: 'normal',
              ...fontStyles
            }}
          >
            {promotionPrimaryMetric}
          </span>
          {/* Percentage change next to title, like 9.02% */}
          {!_isNil(promotionRawChange) && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                color:
                  theme.colors[
                    +promotionRawChange.toFixed(3) === 0 ? 'secondary' : promotionRawChange > 0 ? 'success' : 'error'
                  ]
              }}
            >
              <div>
                {/* This logic has to be inline for it to function with the static rendering */}
                {+promotionRawChange.toFixed(3) === 0 ? (
                  <div
                    style={{
                      transform: 'rotate(0deg)', // pointing to the right
                      color: theme.colors.primary
                    }}
                  >
                    {IconNoChange}
                  </div>
                ) : promotionRawChange > 0 ? (
                  <div style={{ color: theme.colors.success }}>{IconIncrease}</div> // pointing up
                ) : (
                  <div
                    style={{
                      transform: 'rotate(180deg)', // pointing down
                      color: theme.colors.error
                    }}
                  >
                    {IconDecrease}
                  </div>
                )}
              </div>

              <div
                style={{
                  marginLeft: theme.spacing.xs,
                  fontSize: '12px',
                  ...fontStyles
                }}
              >
                {promotionFormattedChange}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PromotionHoverTooltip;
